import { useState, useRef, useCallback } from "react";
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";

const BATCH_SIZE = 100;

const WARNINGS_QUERY = `
    query searchUnitsWarnings($conditions: [UnitSearchInput], $limit: Int) {
        result: searchUnits(conditions: $conditions, limit: $limit) {
            items {
                id
                maintenanceWarnings
                isOffline
                sdCard
            }
        }
    }
`;

/**
 * Fetches maintenanceWarnings, isOffline and sdCard on-demand for a set of unit IDs.
 * These fields are excluded from the main list query to avoid API timeouts on large datasets.
 *
 * Usage:
 *   const { warningsMap, fetchWarningsForIds, resetWarnings } = useWarningsData();
 *   // warningsMap[unitId] = { maintenanceWarnings, isOffline, sdCard } | undefined (not yet loaded)
 */
export const useWarningsData = () => {
    const [warningsMap, setWarningsMap] = useState({});
    // Track IDs already fetched to avoid duplicate requests across infinite scroll pages
    const fetchedIdsRef = useRef(new Set());
    const activeControllersRef = useRef([]);

    const fetchWarningsForIds = useCallback(async (ids) => {
        const newIds = ids.filter(id => id != null && !fetchedIdsRef.current.has(id));
        if (newIds.length === 0) return;

        // Mark as fetched immediately to prevent duplicate calls from concurrent re-renders
        newIds.forEach(id => fetchedIdsRef.current.add(id));

        // Split into batches to keep query conditions manageable
        const batches = [];
        for (let i = 0; i < newIds.length; i += BATCH_SIZE) {
            batches.push(newIds.slice(i, i + BATCH_SIZE));
        }

        for (const batch of batches) {
            const controller = new AbortController();
            activeControllersRef.current.push(controller);

            try {
                const data = await clientQuery(
                    WARNINGS_QUERY,
                    {
                        conditions: [{ fields: ["id"], terms: batch, exact: true, conditionOperator: "OR" }],
                        limit: BATCH_SIZE,
                    },
                    endpoints.GRAPHQL_GENERAL,
                    undefined,
                    controller.signal
                );

                const items = data?.result?.items;
                if (items?.length) {
                    setWarningsMap(prev => {
                        const updated = { ...prev };
                        items.forEach(item => {
                            updated[item.id] = {
                                maintenanceWarnings: item.maintenanceWarnings,
                                isOffline: item.isOffline,
                                sdCard: item.sdCard,
                            };
                        });
                        return updated;
                    });
                }
            } catch (err) {
                if (!err?.aborted) {
                    // Remove from fetched set so the user can retry by refreshing the table
                    batch.forEach(id => fetchedIdsRef.current.delete(id));
                }
            } finally {
                activeControllersRef.current = activeControllersRef.current.filter(c => c !== controller);
            }
        }
    // Only uses refs and the stable setState setter — empty deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Call when the table resets to start fresh (conditions cleared, explicit reload after mutations)
    const resetWarnings = useCallback(() => {
        activeControllersRef.current.forEach(ctrl => ctrl.abort());
        activeControllersRef.current = [];
        fetchedIdsRef.current.clear();
        setWarningsMap({});
    // Only uses refs and the stable setState setter — empty deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { warningsMap, fetchWarningsForIds, resetWarnings };
};
